import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * The page's read-only identity, derived from the nested route
 * `/packages/:packageId/eval3/(workspaces/:workspaceId(/eval/:evalId)?)?`:
 *   - `packageId` — the package every query is scoped to.
 *   - `calculationId` — the `EvaluationCalculation` id (the route `:evalId`), or
 *     `null` in workspace / package mode (no `:evalId`). Sent as `calculationId` on the
 *     filter / table / summary / map requests to run the BE in calc mode; never
 *     `evaluationId` (the BE 422s that Eval 2 id).
 *   - `workspaceId` — the open workspace folder (the route `:workspaceId`), or `null`.
 *     An eval always sits UNDER its workspace, so when `calculationId` is set this is
 *     its owning workspace; when `calculationId` is null but this is set, it's the
 *     management/browse view (package-level content beneath the folder's eval list) —
 *     OR, when `isDraft`, the workspace a from-scratch draft is being composed under.
 *   - `isDraft` — a client-only from-scratch eval (Model A) is being composed under
 *     `workspaceId` (the route `eval/new` sentinel). `calculationId` is null (no BE row
 *     yet); Calculate persists it and routes to the real `eval/:id`. Route-derived, so
 *     the draft survives reload (as a fresh new-eval form) and needs no atom.
 *
 * All four are set once per navigation and read by many descendants — so they live in
 * context, not prop-drilled or in a module-global atom. Eval-URL resolution is DUAL: the
 * SERVER (`Packages::Eval3Controller`) 404s a wrong id and redirects a missing one on cold
 * loads / direct links / refresh, and `Eval3LandingResolver` does the same CLIENT-SIDE for
 * in-app nav (bare `/eval3` → the latest eval, no full-page reload). Either way the URL
 * reaching THIS content tree is already valid + canonical, so consumers can trust this
 * identity and never implement fallback logic. The provider supplies the values
 * synchronously on first render, so the deep query hooks see them before their first fetch.
 */
export interface Eval3PageValue {
  packageId: number;
  calculationId: number | null;
  workspaceId: number | null;
  isDraft: boolean;
  /**
   * The pinned "Saved Evaluations" folder is selected (the `saved-evals` route) — the center
   * shows the package-wide saved-evals browse view. Route-derived (like `isDraft`), so it needs
   * no atom and survives reload. `calculationId`/`workspaceId` are null in this view. (P8 Save.)
   */
  isSavedEvalsView: boolean;
}

const Eval3PageContext = createContext<Eval3PageValue | null>(null);

interface Eval3PageProviderProps {
  packageId: number;
  calculationId: number | null;
  /** The open workspace folder (workspaces route), or null. Defaults to null. */
  workspaceId?: number | null;
  /** A from-scratch draft (`eval/new`) is being composed under `workspaceId`. */
  isDraft?: boolean;
  /** The Saved Evaluations browse route (`saved-evals`) is active. Defaults to false. */
  isSavedEvalsView?: boolean;
  children: ReactNode;
}

/**
 * Provides the page identity (`packageId` + `calculationId` + `workspaceId` + `isDraft`)
 * to the eval3-new tree. Mounted at the page root (`page.tsx`) from the parsed route
 * params. The value is memoized on the primitives so consumers don't re-render on
 * unrelated renders.
 *
 * @param {Eval3PageProviderProps} props - The package id, calc id (or null), workspace id (or null), draft flag, and children.
 * @return {JSX.Element} The provider wrapping the page.
 */
export function Eval3PageProvider({
  packageId,
  calculationId,
  workspaceId = null,
  isDraft = false,
  isSavedEvalsView = false,
  children,
}: Eval3PageProviderProps) {
  const value = useMemo(
    () => ({
      packageId,
      calculationId,
      workspaceId,
      isDraft,
      isSavedEvalsView,
    }),
    [packageId, calculationId, workspaceId, isDraft, isSavedEvalsView]
  );
  return (
    <Eval3PageContext.Provider value={value}>
      {children}
    </Eval3PageContext.Provider>
  );
}

/**
 * Reads the page identity. Throws when called outside `Eval3PageProvider` so a
 * provider-placement bug surfaces immediately (every eval3-new query hook + the
 * dataset accordion read this).
 *
 * @return {Eval3PageValue} The current `{ packageId, calculationId }`.
 * @throws {Error} When used outside an `Eval3PageProvider`.
 */
export function useEval3Page(): Eval3PageValue {
  const ctx = useContext(Eval3PageContext);
  if (ctx === null) {
    throw new Error('useEval3Page must be used inside an Eval3PageProvider');
  }
  return ctx;
}

/**
 * Reads the page identity when a provider is present, and null otherwise. For a hook whose
 * caller can pass the identity itself (the tearsheet, which is handed its packageId).
 *
 * @return {Eval3PageValue | null} The current `{ packageId, calculationId }`, or null.
 */
export function useOptionalEval3Page(): Eval3PageValue | null {
  return useContext(Eval3PageContext);
}
